<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessages, useToolCalls } from '@langchain/vue';
import CopilotMessage from './CopilotMessage.vue';
import CopilotToolCall from './CopilotToolCall.vue';

const props = defineProps({
  subagent: { type: Object, required: true },
  stream: { type: Object, required: true },
});

const { t } = useI18n();

const messages = useMessages(props.stream, () => props.subagent);
const toolCalls = useToolCalls(props.stream, () => props.subagent);

const statusConfig = computed(() => {
  const map = {
    pending: {
      icon: 'i-lucide-clock',
      color: 'text-n-slate-11',
      label: t('COPILOT.SUBAGENT_PENDING'),
    },
    running: {
      icon: 'i-lucide-loader-2',
      color: 'text-n-violet-11',
      label: t('COPILOT.SUBAGENT_RUNNING'),
    },
    complete: {
      icon: 'i-lucide-check',
      color: 'text-n-green-11',
      label: t('COPILOT.SUBAGENT_COMPLETE'),
    },
    error: {
      icon: 'i-lucide-x',
      color: 'text-n-ruby-11',
      label: t('COPILOT.SUBAGENT_ERROR'),
    },
  };
  return map[props.subagent.status] || map.pending;
});
</script>

<template>
  <div class="rounded-lg border border-n-weak bg-n-slate-2 overflow-hidden">
    <!-- Subagent header -->
    <div class="flex items-center gap-2 px-3 py-2 bg-n-slate-3">
      <div class="size-4" :class="[statusConfig.icon, statusConfig.color]" />
      <span class="text-sm font-medium text-n-slate-12">
        {{ subagent.name }}
      </span>
      <span class="text-xs ml-auto" :class="statusConfig.color">
        {{ statusConfig.label }}
      </span>
    </div>

    <!-- Subagent content -->
    <div class="px-3 py-2 space-y-2 max-h-64 overflow-y-auto">
      <template v-for="(msg, index) in messages" :key="msg.id ?? index">
        <CopilotMessage :message="msg" />
      </template>

      <CopilotToolCall
        v-for="tool in toolCalls"
        :key="tool.id"
        :tool-call="tool"
      />

      <div
        v-if="!messages.length && subagent.status === 'running'"
        class="flex items-center gap-2.5 py-1.5"
      >
        <div class="i-lucide-loader-2 size-4 text-n-violet-11 animate-spin" />
        <span class="text-sm font-medium text-n-slate-12">{{
          t('COPILOT.THINKING')
        }}</span>
      </div>
    </div>
  </div>
</template>
